import { apiClient } from "../api";
import { socketService } from "../api/socket";
import {
  ChatMessage,
  ChatRoom,
  ChatAttachmentEvent,
  TicketResponse,
} from "../types/chat";
import { AxiosProgressEvent } from "axios";
import { soundNotificationService } from "./SoundNotificationService";

class ChatService {

  private messageQueue: Map<string, ChatMessage[]> = new Map();
  private activeSubscriptions: Map<string, Set<string>> = new Map(); // userId -> Set of chatIds
  private chatSessions: Map<
    string,
    { userId?: string; guestEmail?: string; role: string }
  > = new Map();
  private staffAvailability: Map<string, boolean> = new Map();
  private onlineStaff: Map<string, { isOnline: boolean; role: string; availableForChat: boolean; }> = new Map();
  private lastPendingChatCount: number = 0;
  private static instance: ChatService;

  static getInstance(): ChatService {
    if(!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  private constructor() {}

  updateStaffStatus(userId: string, isOnline: boolean, role: string, availableForChat: boolean): void {
    this.onlineStaff.set(userId, { isOnline, role, availableForChat });
  }

  getStaffStatus(userId: string) {
    return this.onlineStaff.get(userId);
  }

  getOnlineStaff(): Map<string, { isOnline: boolean; role: string }> {
    return this.onlineStaff;
  }

  private validateChatSession(chatId: string, identifier: string): boolean {
    const session = this.chatSessions.get(chatId);
    return session?.userId === identifier || session?.guestEmail === identifier;
  }

  private trackChatSession(
    chatId: string,
    identifier: string,
    role: string,
    isGuest: boolean = false
  ): void {
    this.chatSessions.set(
      chatId,
      isGuest ? { guestEmail: identifier, role } : { userId: identifier, role }
    );

    if (!this.activeSubscriptions.has(identifier)) {
      this.activeSubscriptions.set(identifier, new Set());
    }
    this.activeSubscriptions.get(identifier)?.add(chatId);
  }

  handlePendingChatUpdate(pendingChats: ChatRoom[]): boolean {
    const currentCount = pendingChats.length;
    const hasNewChat = currentCount > this.lastPendingChatCount && this.lastPendingChatCount >= 0;

    // Play sound if there are new pending chats (list is already scoped to
    // this staff member's department via getUnassignedChats())
    if (hasNewChat) {
      soundNotificationService.playSound('pendingChat');
    }

    this.lastPendingChatCount = currentCount;
    return hasNewChat;
  }

  // Initialize pending chat count
  initializePendingChatCount(count: number): void {
    this.lastPendingChatCount = count;
  }

  async toggleAvailability(available: boolean): Promise<void> {
    const response = await apiClient.post('/user/toggle-chat-availability', { available });
    return response.data;
  }

  setupSocketEventHandlers(handlers: {
    onStaffAvailabilityChanged?: (data: { userId: string; availableForChat: boolean }) => void;
    onStaffStatusUpdate?: (data: { userId: string; availableForChat: boolean }) => void;
    onChatAccepted?: (data: { chatId: string; staffId: string }) => void;
    onNewPendingChat?: (data: { chat_room: ChatRoom }) => void;
  }) {
    socketService.socketInstance?.on('staff.availability.changed', (data) => {
      this.updateStaffStatus(
        data.userId,
        this.getStaffStatus(data.userId)?.isOnline || false,
        this.getStaffStatus(data.userId)?.role || '',
        data.availableForChat
      );
      if (handlers.onStaffAvailabilityChanged) {
        handlers.onStaffAvailabilityChanged(data);
      }
    });

    socketService.socketInstance?.on('staff.status.update', (data) => {
      this.updateStaffStatus(
        data.userId,
        true,
        this.getStaffStatus(data.userId)?.role || '',
        data.availableForChat
      );
      if (handlers.onStaffStatusUpdate) {
        handlers.onStaffStatusUpdate(data);
      }
    });

    socketService.socketInstance?.on('chat.accepted', (data) => {
      if (handlers.onChatAccepted) {
        handlers.onChatAccepted(data);
      }
    });

    socketService.socketInstance?.on('new.chat', async (data) => {
      // Sound plays from handlePendingChatUpdate(), which is scoped to this
      // staff member's department (via getUnassignedChats()); this event is
      // broadcast to every connected staff member regardless of department.
      if (data.chat_room?.status === 'pending' && handlers.onNewPendingChat) {
        handlers.onNewPendingChat(data);
      }
    });
  }

  removeSocketEventHandlers() {
    socketService.socketInstance?.off('staff.availability.changed');
    socketService.socketInstance?.off('staff.status.update');
    socketService.socketInstance?.off('chat.accepted');
    socketService.socketInstance?.off('new.chat');
  }

  isStaffAvailableForChat(userId: string): boolean {
    const status = this.getStaffStatus(userId);
    return status?.isOnline && status?.availableForChat || false;
  }

  getAvailableStaffCount(): number {
    return Array.from(this.onlineStaff.values())
      .filter(status => status.isOnline && status.availableForChat)
      .length;
  }


  async checkStaffAvailability(): Promise<{
    has_online_staff: boolean;
    departments: string[];
    department_roles: { [key: string]: number };
    available_staff: Array<{
      id: number;
      name: string;
      role: string;
      role_id: number;
      available_for_chat: boolean;
    }>;
  }> {
    const response = await apiClient.get("/chat/check-availability");
    response.data.available_staff.forEach((staff: any) => {
      this.updateStaffStatus(staff.id, true, staff.role, staff.available_for_chat);
    });
    return response.data;
  }


  async createTicketFromChat(data: {
    subject: string;
    description: string;
    department_role_id: number;
    priority: "low" | "medium" | "high" | "urgent";
    guest_email?: string;
    guest_name?: string;
    user_id?: string;
  }): Promise<any> {
    const response = await apiClient.post("/tickets/create-from-chat", data);
    return response.data;
  }

  async createGuestTicket(data: {
    subject: string;
    description: string;
    priority: "low" | "medium" | "high" | "urgent";
    guest_email: string;
    department_role_id: number;
    guest_name: string;
    phone: string;
    is_guest: boolean;
  }): Promise<TicketResponse> {
    const response = await apiClient.post("/tickets/guest", data);
    return response.data;
  }

  async uploadAttachment(
    chatId: string,
    files: FileList,
    isGuest: boolean = false,
    guestEmail?: string
  ): Promise<ChatMessage> {
    const formData = new FormData();

    Array.from(files).forEach((file) => {
      formData.append("attachments[]", file);
    });

    const endpoint = isGuest
      ? `/chat/guest/${chatId}/upload`
      : `/chat/${chatId}/upload`;

    if (isGuest && guestEmail) {
      formData.append("guest_email", guestEmail);
    }

    const response = await apiClient.post(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          socketService.socketInstance?.emit("attachment-upload-progress", {
            chatId,
            fileName: files[0].name,
            progress: percentCompleted,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });

    return response.data.chat_message;
  }


  async downloadAttachment(messageId: number, fileName: string): Promise<Blob> {
    const response = await apiClient.get(
      `/chat/messages/${messageId}/download/${fileName}`,
      { responseType: "blob" }
    );
    return response.data;
  }

  async getAttachments(chatId: string): Promise<ChatMessage[]> {
    const response = await apiClient.get(`/chat/${chatId}/attachments`);
    return response.data;
  }

  private removeChatSession(chatId: string, identifier: string): void {
    this.chatSessions.delete(chatId);
    this.activeSubscriptions.get(identifier)?.delete(chatId);
    if (this.activeSubscriptions.get(identifier)?.size === 0) {
      this.activeSubscriptions.delete(identifier);
    }
  }

  private cleanupChatSession(chatId: string, userId: string): void {
    this.removeChatSession(chatId, userId);
    this.messageQueue.delete(chatId);
    localStorage.removeItem(`activeChatId-${userId}`);
  }

  setupAttachmentEventListeners(
    chatId: string,
    callbacks: {
      onUploadStart: (data: ChatAttachmentEvent) => void;
      onUploadComplete: (data: ChatAttachmentEvent) => void;
      onUploadError: (data: ChatAttachmentEvent) => void;
    }
  ): void {
    socketService.socketInstance?.on(`attachment-upload-started`, (data) => {
      if (data.chatId === chatId) {
        callbacks.onUploadStart(data);
      }
    });

    socketService.socketInstance?.on(`attachment-upload-completed`, (data) => {
      if (data.chatId === chatId) {
        callbacks.onUploadComplete(data);
      }
    });

    socketService.socketInstance?.on(`attachment-upload-error`, (data) => {
      if (data.chatId === chatId) {
        callbacks.onUploadError(data);
      }
    });
  }
  removeAttachmentEventListeners(): void {
    socketService.socketInstance?.off("attachment-upload-started");
    socketService.socketInstance?.off("attachment-upload-completed");
    socketService.socketInstance?.off("attachment-upload-error");
  }

  async getActiveChats(): Promise<ChatRoom[]> {
    const response = await apiClient.get("/chat/active");
    return response.data;
  }

  async getEndedChats(): Promise<ChatRoom[]> {
    const response = await apiClient.get("/chat/ended");
    return response.data;
  }


  async getUnassignedChats(): Promise<ChatRoom[]> {
    const response = await apiClient.get("/chat/unassigned");
    return response.data;
  }



  async acceptChat(chatId: string): Promise<{ chat_room: ChatRoom }> {
    const response = await apiClient.post(`/chat/${chatId}/accept`);
    return response.data;
  }


  async getChatHistory(
    chatId: string,
    page: number = 1
  ): Promise<{
    messages: ChatMessage[];
    chat_room: ChatRoom;
    pagination?: { current_page: number; last_page: number; per_page: number; total: number };
  }> {
    const response = await apiClient.get(`/chat/${chatId}/history`, {
      params: { page },
    });
    return response.data;
  }

  async sendMessage(chatId: string, content: ChatMessage["content"], isGuest: boolean = false, guestEmail?: string): Promise<ChatMessage> {
    const messageData = {
      content,
      is_guest: isGuest,
      guest_email: guestEmail
    };

    const response = await apiClient.post(`/chat/${chatId}/message`, messageData);
    return response.data.chat_message;
  }

  async startUserChat(data: { staff_role: string }, userId: string): Promise<{ chat_id: string; message: string }> {
    const response = await apiClient.post("/chat/user/start", data);
    this.trackChatSession(response.data.chat_id, userId, "user");
    return response.data;
  }

  async joinSupportStaff(
    chatId: string,
    userId: string
  ): Promise<{ message: string; chat_room: ChatRoom }> {
    const response = await apiClient.post(`/chat/${chatId}/join-support-staff`);
    this.trackChatSession(chatId, userId, "staff");
    return response.data;
  }

  async leaveSupportStaff(chatId: string, userId: string): Promise<{ message: string }> {
    const response = await apiClient.post(`/chat/${chatId}/leave-support-staff`);

    this.removeChatSession(chatId, userId);
    socketService.leaveChat(chatId);
    return response.data;
  }


  async transferChat(chatId: string, staffId: number): Promise<{ message: string; chat_room: ChatRoom }> {
    const response = await apiClient.post(`/chat/${chatId}/transfer`, {
      staff_id: staffId,
    });
    return response.data;
  }

  async endChat(chatId: string, userId: string): Promise<{ message: string }> {
    const response = await apiClient.post(`/chat/${chatId}/end`);

    this.cleanupChatSession(chatId, userId);
    socketService.leaveChat(chatId);
    return response.data;
  }

  async markMessagesAsRead(chatId: string): Promise<{ message: string }> {
    const response = await apiClient.post(`/chat/${chatId}/read`);
    return response.data;
  }

  async getAvailableStaffRoles(): Promise<{ roles: string[]; staff: any[] }> {
    const response = await apiClient.get("/chat/staff-roles");
    response.data.staff.forEach((staff: any) => {
      this.updateStaffStatus(staff.id, staff.is_online, staff.role, staff.available_for_chat);
    });
    return response.data;
  }

  subscribeToChat(chatId: string, userId: string, userRole: string): void {
    if (!this.validateChatSession(chatId, userId)) {
      this.trackChatSession(chatId, userId, userRole);
    }

    socketService.joinChat(chatId, userRole);
  }


  unsubscribeFromChat(chatId: string, userId: string): void {
    socketService.leaveChat(chatId);
    this.cleanupChatSession(chatId, userId);
  }


  async startGuestChat(data: {
    name: string;
    email: string;
    phone: string;
    subject: string;
    staff_role: string;
    department: string;
  }): Promise<{ chat_id: string; message: string }> {
    const response = await apiClient.post("/chat/guest/start", data);
    this.trackChatSession(response.data.chat_id, data.email, "guest", true);
    return response.data;
  }


  async getGuestChatHistory(chatId: string, email: string): Promise<{ messages: ChatMessage[]; chat_room: ChatRoom }> {
    const response = await apiClient.get(`/chat/guest/${chatId}/history`, {
      params: { email }
    });
    return response.data;
  }


  async getGuestActiveChats(email: string): Promise<{
    chat: {
      chat_id: string;
      status: string;
      department: string;
      subject: string;
      guest_name: string;
      guest_email: string;
      guest_phone: string;
    } | null;
  }> {
    const response = await apiClient.get("/chat/guest/active-chats", {
      params: { email }
    });
    return response.data;
  }


  async endGuestChat(chatId: string, guestEmail: string): Promise<{ message: string; status: string }> {
    const response = await apiClient.post(`/chat/guest/${chatId}/end`, {
      guest_email: guestEmail,
      is_guest: true
    });

    this.cleanupChatSession(chatId, guestEmail);
    socketService.leaveChat(chatId);

    return response.data;
  }




  async getAvailableStaffForGuest(): Promise<{
    roles: string[];
    staff: any[];
  }> {
    const response = await apiClient.get("/chat/guest/available-staff");
    return response.data;
  }

  async sendGuestMessage(chatId: string, content: ChatMessage["content"], guestEmail: string): Promise<ChatMessage> {
    const response = await apiClient.post(`/chat/guest/${chatId}/message`, {
      content,
      is_guest: true,
      guest_email: guestEmail
    });
    return response.data.chat_message;
  }




  subscribeToGuestChat(chatId: string, guestEmail: string): void {
    if (!this.validateChatSession(chatId, guestEmail)) {
      this.trackChatSession(chatId, guestEmail, "guest", true);
    }

    socketService.joinGuestChat(chatId, guestEmail);
  }



  cleanup(): void {
    this.messageQueue.clear();
    this.activeSubscriptions.clear();
    this.chatSessions.clear();
    this.onlineStaff.clear();
    this.staffAvailability.clear();
    socketService.disconnect();
  }
}

export const chatService = ChatService.getInstance();
